import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, HeartPulse, Users, GraduationCap, HeartHandshake, Trophy } from "lucide-react";

const AREAS = [
  { id: "education", icon: BookOpen, t: "Education", s: "Quality education for a brighter tomorrow.", img: "/home/education.jpg", to: "/programs#child-education", tone: "bg-blue-50 text-blue-600", arrow: "bg-blue-50 text-blue-600", card: "from-blue-50/70" },
  { id: "healthcare", icon: HeartPulse, t: "Healthcare", s: "Better health for stronger communities.", img: "/home/health.jpg", to: "/programs#healthcare", tone: "bg-emerald-50 text-emerald-600", arrow: "bg-emerald-50 text-emerald-600", card: "from-emerald-50/70" },
  { id: "social", icon: Users, t: "Social Support", s: "Helping vulnerable communities thrive.", img: "/home/social.jpg", to: "/programs#women", tone: "bg-orange-50 text-orange-500", arrow: "bg-orange-50 text-orange-500", card: "from-orange-50/70" },
  { id: "internships", icon: GraduationCap, t: "Internships & Test Series", s: "Skill development for future leaders.", img: "/home/internship.jpg", to: "/internships", tone: "bg-violet-50 text-violet-600", arrow: "bg-violet-50 text-violet-600", card: "from-violet-50/70" },
];

const STATS = [
  { icon: Users, k: "10,000+", v: "Lives Impacted" },
  { icon: Users, k: "500+", v: "Active Volunteers" },
  { icon: HeartHandshake, k: "20+", v: "Community Programs" },
  { icon: Trophy, k: "5+", v: "Years of Impact" },
];

function AreaCard({ a, i }) {
  return (
    <Link to={a.to} className={`group home-rise rounded-2xl overflow-hidden bg-gradient-to-b ${a.card} to-white border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-300 flex flex-col`} style={{ animationDelay: `${0.1 * i}s` }} data-testid={`focus-${a.id}`}>
      <div className="p-5 flex items-start gap-4">
        <div className={`w-14 h-14 shrink-0 rounded-xl ${a.tone} flex items-center justify-center`}><a.icon size={26} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="heading text-lg font-bold text-[#0b1637] leading-tight">{a.t}</h3>
          <p className="mt-1 text-sm text-slate-600 leading-snug">{a.s}</p>
        </div>
        <span className={`w-10 h-10 shrink-0 rounded-full ${a.arrow} flex items-center justify-center group-hover:translate-x-1 transition-transform`}><ArrowRight size={18} /></span>
      </div>
      <div className="mt-auto h-40 overflow-hidden">
        <img src={a.img} alt={a.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
    </Link>
  );
}

export function FocusAreas() {
  return (
    <section className="bg-white pt-16 pb-10" data-testid="focus-areas">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.25em] text-[#0b1637]"><span className="w-8 h-0.5 bg-orange-500" /> What We Do</div>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <h2 className="heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#0b1637]">Our Focus Areas</h2>
              <p className="text-sm sm:text-base text-slate-600">We work towards creating lasting change through focused initiatives.</p>
            </div>
          </div>
          <Link to="/programs" className="group border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 transition-colors" data-testid="explore-all-programs">
            Explore All Programs <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {AREAS.map((a, i) => <AreaCard key={a.id} a={a} i={i} />)}
        </div>
      </div>
    </section>
  );
}

export function ImpactBand() {
  return (
    <section className="bg-white pb-16 px-4 sm:px-6 lg:px-8" data-testid="impact-band">
      <div className="max-w-7xl mx-auto bg-[#0b1637] text-white rounded-[28px] px-6 py-7 grid grid-cols-2 lg:grid-cols-4 gap-y-6 lg:divide-x divide-white/15">
        {STATS.map((s) => (
          <div key={s.v} className="flex items-center justify-center gap-4 px-4" data-testid={`impact-${s.v.split(" ")[0].toLowerCase()}`}>
            <s.icon size={36} strokeWidth={1.6} className="shrink-0" />
            <div><div className="heading text-2xl sm:text-3xl font-black">{s.k}</div><div className="text-sm text-slate-300">{s.v}</div></div>
          </div>
        ))}
      </div>
    </section>
  );
}
